// istanbul ignore file
/* eslint-disable @typescript-eslint/restrict-template-expressions */
/* eslint-disable react/prop-types */
/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import React, { useCallback, useRef } from 'react'
import { type StylesConfig } from 'react-select'
import AsyncSelect from 'react-select/async'
import './GenXDropdown.css'
import LanguageStrings from '../i18n/locales'
import { CircularProgress } from '@mui/material'

const ComponentStrings = LanguageStrings().Components

export const customStyles = (width?: string | number, disabled?: boolean): StylesConfig => ({
  control: (provided: any, { isFocused }) => ({
    ...provided,
    border: isFocused ? '2px solid #64C3FF' : '1px solid #404040',
    borderRadius: '4px',
    boxShadow: 'none',
    backgroundColor: '#272727',
    width: width !== undefined ? width : 260,
    cursor: 'pointer',
    height: 40,
    minHeight: 40,
    padding: 0,
    color: '#ffffff',
    opacity: disabled ? '0.5' : '1'
  }),
  option: (styles: any, { isSelected, isFocused }) => ({
    ...styles,
    color: isSelected ? '#64C3FF' : '#f0f0f0',
    backgroundColor: (isSelected) ? '#1792E51A' : (isFocused) ? '#303030' : '#404040',
    width: width !== undefined ? width : 260,
    overflow: 'ellipses',
    padding: '5px 8px'
  }),
  menu: (provided: any) => ({
    ...provided,
    boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)',
    width: width !== undefined ? width : 260,
    backgroundColor: '#404040',
    zIndex: 9999, // Ensure high z-index to appear above other elements
    position: 'absolute' // Force absolute positioning
  }),
  menuPortal: (provided: any) => ({
    ...provided,
    zIndex: 9999 // Consistent z-index for the portal
  }),
  valueContainer: (styles: any) => ({
    ...styles,
    height: 40,
    paddingLeft: '8px'
  }),
  indicatorsContainer: (styles: any) => ({
    ...styles,
    height: 40,
    paddingRight: '4px'
  }),
  placeholder: (styles: any) => ({
    ...styles,
    fontSize: '13px'
  })
})

export interface Option {
  value: string
  label: string
}

const injectLabel = (label: string | undefined): JSX.Element => {
  return <span style={{ fontSize: '15px', paddingBottom: 10 }}>{label}</span>
}

interface AsyncGenXDropdownProps {
  options: Option[]
  value: Option | null
  onSelect: (selectedValue: Option, index?: number) => void
  placeholder?: string
  width?: number | string
  height?: number
  label?: string
  disabled?: boolean
  fetchOptions: (inputValue: string) => Promise<Option[]>
}

export function AsyncGenXDropdown ({ options, onSelect, value, placeholder, width, label, height, disabled, fetchOptions }: AsyncGenXDropdownProps): JSX.Element {
  // Track the timeout ID for debouncing
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Create a state to track all available options including searched ones
  const [allOptions, setAllOptions] = React.useState<Option[]>(options)

  // Update allOptions when options prop changes
  React.useEffect(() => {
    setAllOptions(prevOptions => {
      // Preserve selected value in options if it exists and isn't in the new options
      if (value && !options.some(opt => opt.value === value.value)) {
        return [...options, value]
      }
      return options
    })
  }, [options, value])

  // Clean up timeout on component unmount
  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const handleChange = (selectedOption: Option, index?: number): void => {
    if (selectedOption != null) {
      // Add the selected option to allOptions if it's not already there
      setAllOptions(prevOptions => {
        if (!prevOptions.some(opt => opt.value === selectedOption.value)) {
          return [...prevOptions, selectedOption]
        }
        return prevOptions
      })
      onSelect(selectedOption, index)
    }
  }

  const LoadingIndicator = (): JSX.Element => {
    return (
      <CircularProgress size={20} style={{ color: '#64C3FF', marginRight: 10 }} />
    )
  }

  // Implement debounced loadOptions function for react-select's AsyncSelect
  const loadOptions = useCallback(async (inputValue: string): Promise<Option[]> => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    // Return a new promise that will resolve after the debounce delay
    return await new Promise((resolve) => {
      // Set a new timeout - 300ms is a common debounce delay for search inputs
      timeoutRef.current = setTimeout(() => {
        void (async () => {
          const results = await fetchOptions(inputValue)

          // Update allOptions with any new options from the search
          if (results.length > 0) {
            setAllOptions(prevOptions => {
              const newOptions = results.filter(
                result => !prevOptions.some(opt => opt.value === result.value)
              )
              return [...prevOptions, ...newOptions]
            })
          }

          resolve(results)
        })()
      }, 800)
    })
  }, [fetchOptions, setAllOptions])

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {label !== undefined && injectLabel(label)}
      <AsyncSelect
        isDisabled={disabled}
        value={value}
        // Use allOptions that include both initial and searched options
        defaultOptions={allOptions.length > 0 ? allOptions : true}
        cacheOptions
        classNames={{
          option: (props) => {
            return (props.data !== null && props.data !== undefined && (props.data as { label?: string }).label !== undefined && (props.data as { label?: string }).label !== undefined) ? `${(props.data as { label?: string }).label}-option` : 'genx-dropdown-option'
          }
        }}
        isSearchable
        inputId={`genx-dropdown ${label}-genx-dropdown`}
        maxMenuHeight={height !== undefined ? height : 150}
        // @ts-expect-error - typescript is not able to determine the type
        onChange={handleChange}
        placeholder={placeholder !== undefined ? placeholder : ComponentStrings.SelectAnOption}
        components={{ LoadingIndicator }}
        unstyled
        styles={customStyles(width, disabled)}
        menuPortalTarget={document.body} // Render menu in a portal at the document body
        menuPosition="fixed" // Use fixed positioning for menus
        menuPlacement="auto" // Auto place the menu to avoid screen boundaries
        // No need to manage input state separately since the input value is passed to loadOptions
        loadOptions={loadOptions}
        theme={(theme: any) => ({
          ...theme,
          borderRadius: 0,
          height: 40,
          colors: {
            ...theme.colors,
            primary25: 'hotpink',
            neutral0: '#404040'
          }
        })}
      />
    </div>
  )
}
